import React, { Component, createRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  Stack,
  IconButton,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  Delete,
  Save,
  Add,
  Remove,
  ArrowBack,
  QrCode2,
  Create,
  WhatsApp
} from "@mui/icons-material";

// --- Subcomponente do Quadro de Assinatura Isolado (Class Component) ---
class SignaturePad extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.state = {
      isDrawing: false,
      ctx: null
    };
  }

  componentDidMount() {
    this.initCanvas();
    window.addEventListener("resize", this.resizeCanvas);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resizeCanvas);
  }

  initCanvas = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    context.lineWidth = 3;
    context.lineCap = "round";
    context.strokeStyle = "#000000";

    this.setState({ ctx: context }, () => {
      this.resizeCanvas();
    });
  };

  resizeCanvas = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentNode;
    const { ctx } = this.state;

    canvas.width = container.offsetWidth;
    canvas.height = 180;

    if (ctx) {
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#000000";
    }
  };

  getCoords = (e) => {
    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  startDrawing = (e) => {
    e.preventDefault();
    const { ctx } = this.state;
    if (!ctx) return;

    const { x, y } = this.getCoords(e);
    this.setState({ isDrawing: true });
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  draw = (e) => {
    e.preventDefault();
    const { isDrawing, ctx } = this.state;
    if (!isDrawing || !ctx) return;

    const { x, y } = this.getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  stopDrawing = () => {
    const { isDrawing, ctx } = this.state;
    if (!isDrawing || !ctx) return;

    this.setState({ isDrawing: false });
    ctx.closePath();

    if (this.props.onSave) {
      this.props.onSave(this.canvasRef.current.toDataURL("image/png"));
    }
  };

  clearCanvas = () => {
    const { ctx } = this.state;
    const canvas = this.canvasRef.current;
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (this.props.onClear) {
      this.props.onClear();
    }
  };

  render() {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "background.paper" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="600" color="text.primary">
            {this.props.title}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="text"
              color="error"
              startIcon={<Delete />}
              onClick={this.clearCanvas}
            >
              Limpar
            </Button>
            {this.props.onClose && (
              <Button size="small" variant="text" color="inherit" onClick={this.props.onClose}>
                Fechar
              </Button>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: 180,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            touchAction: "none",
            backgroundColor: "#fafafa",
            cursor: "crosshair"
          }}
        >
          <canvas
            ref={this.canvasRef}
            style={{ display: "block" }}
            onTouchStart={this.startDrawing}
            onTouchMove={this.draw}
            onTouchEnd={this.stopDrawing}
            onMouseDown={this.startDrawing}
            onMouseMove={this.draw}
            onMouseUp={this.stopDrawing}
            onMouseOut={this.stopDrawing}
          />
        </Box>
      </Paper>
    );
  }
}

// --- Componente Principal ---
class Assinatura extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Controla se o pad local está aberto na tela do PC
      showLocatarioPad: false,
      showWitnessPadId: null,

      locatarioSig: null,
      testemunhas: [], // Inicia sem nenhuma testemunha na lista

      // Controla o status de como foi enviado/feito (ex: 'whatsapp', 'qrcode', 'local')
      locatarioStatus: null,

      // Modal de Compartilhamento via QR Code
      shareModal: {
        open: false,
        title: "",
        link: ""
      }
    };
  }

  // Gera o link dinâmico da assinatura baseado no ID do signatário
  generateSignatureLink = (identifier) => {
    return `https://seuapp.com/assinar-remoto/${identifier}`;
  };

  // Abre o quadro físico para o Locatário assinar no PC
  handleOpenLocatarioLocal = () => {
    this.setState({ showLocatarioPad: true, locatarioStatus: 'local' });
  };

  // Dispara o QR Code do Locatário (Fecha o pad físico se estiver aberto)
  handleLocatarioQRCode = () => {
    const link = this.generateSignatureLink('locatario');
    this.setState({
      showLocatarioPad: false,
      locatarioStatus: 'qrcode',
      shareModal: { open: true, title: "Assinatura Remota - Locatário", link }
    });
  };

  // Dispara o WhatsApp do Locatário (Fecha o pad físico se estiver aberto)
  handleLocatarioWhatsApp = () => {
    const link = this.generateSignatureLink('locatario');
    const text = encodeURIComponent(`Olá! Por favor, utilize este link para realizar a sua assinatura digital: ${link}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');

    this.setState({ showLocatarioPad: false, locatarioStatus: 'whatsapp' });
  };

  // Adiciona uma nova linha de testemunha (Em estado limpo de escolha)
  handleAddWitnessRow = () => {
    this.setState((prevState) => {
      const { testemunhas } = prevState;
      const nextId = testemunhas.length > 0 ? Math.max(...testemunhas.map(t => t.id)) + 1 : 1;
      return {
        testemunhas: [
          ...testemunhas,
          { id: nextId, label: `Testemunha ${nextId}`, dataUrl: null, status: null }
        ]
      };
    });
  };

  handleRemoveTestemunha = (id) => {
    this.setState((prevState) => ({
      testemunhas: prevState.testemunhas.filter(t => t.id !== id),
      showWitnessPadId: prevState.showWitnessPadId === id ? null : prevState.showWitnessPadId
    }));
  };

  // Gatilhos de ações das testemunhas
  handleWitnessLocal = (id) => {
    this.setState((prevState) => ({
      showWitnessPadId: id,
      testemunhas: prevState.testemunhas.map(t => t.id === id ? { ...t, status: 'local' } : t)
    }));
  };

  handleWitnessQRCode = (testemunha) => {
    const link = this.generateSignatureLink(`testemunha-${testemunha.id}`);
    this.setState((prevState) => ({
      showWitnessPadId: null, // Garante que fecha o canvas se clicar em QR Code
      testemunhas: prevState.testemunhas.map(t => t.id === testemunha.id ? { ...t, status: 'qrcode' } : t),
      shareModal: { open: true, title: `Assinatura Remota - ${testemunha.label}`, link }
    }));
  };

  handleWitnessWhatsApp = (testemunha) => {
    const link = this.generateSignatureLink(`testemunha-${testemunha.id}`);
    const text = encodeURIComponent(`Olá! Por favor, utilize este link para realizar a assinatura como ${testemunha.label}: ${link}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');

    this.setState((prevState) => ({
      showWitnessPadId: null,
      testemunhas: prevState.testemunhas.map(t => t.id === testemunha.id ? { ...t, status: 'whatsapp' } : t)
    }));
  };

  handleWitnessSave = (id, dataUrl) => {
    this.setState((prevState) => ({
      testemunhas: prevState.testemunhas.map(t => t.id === id ? { ...t, dataUrl } : t)
    }));
  };

  handleCloseShareModal = () => {
    this.setState({ shareModal: { open: false, title: "", link: "" } });
  };

  handleFinalSave = () => {
    const { locatarioSig, testemunhas, locatarioStatus } = this.state;
    const payload = {
      locatario: { signature: locatarioSig, status: locatarioStatus },
      testemunhas: testemunhas.map(t => ({ label: t.label, signature: t.dataUrl, status: t.status }))
    };

    console.log("Dados Prontos para Envio:", payload);

    if (this.props.callBack) {
      this.props.callBack(payload);
    }
  };

  render() {
    const { locatarioSig, testemunhas, showLocatarioPad, showWitnessPadId, shareModal, locatarioStatus } = this.state;
    const { onVoltar } = this.props;

    return (
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Header */}
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onVoltar} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: "600" }}>
              Assinaturas
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Conteúdo Principal */}
        <Box sx={{ maxWidth: 600, mx: "auto", p: 2, mt: 2 }}>

          {/* SEÇÃO DO LOCATÁRIO */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: '700' }}>
            LOCATÁRIO
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={showLocatarioPad ? "contained" : "outlined"}
                startIcon={<Create />}
                fullWidth
                onClick={this.handleOpenLocatarioLocal}
              >
                Assinar no PC
              </Button>
              <IconButton
                color={locatarioStatus === 'qrcode' ? "success" : "primary"}
                onClick={this.handleLocatarioQRCode}
                sx={{ border: '1px solid', borderRadius: 1, px: 1.5 }}
                title="Gerar QR Code"
              >
                <QrCode2 />
              </IconButton>
              <IconButton
                color={locatarioStatus === 'whatsapp' ? "success" : "primary"}
                onClick={this.handleLocatarioWhatsApp}
                sx={{ border: '1px solid', borderRadius: 1, px: 1.5 }}
                title="Enviar por WhatsApp"
              >
                <WhatsApp />
              </IconButton>
            </Stack>

            {/* Renderiza o Pad condicionalmente APENAS se for assinar local */}
            {showLocatarioPad && (
              <Box sx={{ mt: 2 }}>
                <SignaturePad
                  title="Assinatura Digital no Computador"
                  onSave={(dataUrl) => this.setState({ locatarioSig: dataUrl })}
                  onClear={() => this.setState({ locatarioSig: null })}
                  onClose={() => this.setState({ showLocatarioPad: false })}
                />
              </Box>
            )}

            {/* Alertas visuais de status */}
            {!showLocatarioPad && locatarioStatus && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1, fontWeight: '600' }}>
                {locatarioStatus === 'whatsapp' && "✓ Link enviado por WhatsApp para o cliente."}
                {locatarioStatus === 'qrcode' && "✓ QR Code gerado para o cliente."}
              </Typography>
            )}
          </Paper>

          <Divider sx={{ my: 4 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="700">
              TESTEMUNHAS
            </Typography>
          </Divider>

          {/* SEÇÃO DAS TESTEMUNHAS DINÂMICAS */}
          {testemunhas.map((testemunha) => (
            <Paper variant="outlined" key={testemunha.id} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showWitnessPadId === testemunha.id ? 2 : 0 }}>
                <Typography variant="body1" fontWeight="600">
                  {testemunha.label}
                </Typography>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={showWitnessPadId === testemunha.id ? "contained" : "outlined"}
                    startIcon={<Create />}
                    onClick={() => this.handleWitnessLocal(testemunha.id)}
                  >
                    No PC
                  </Button>
                  <IconButton
                    color={testemunha.status === 'qrcode' ? "success" : "primary"}
                    size="small"
                    onClick={() => this.handleWitnessQRCode(testemunha)}
                    sx={{ border: '1px solid', borderRadius: 1 }}
                  >
                    <QrCode2 />
                  </IconButton>
                  <IconButton
                    color={testemunha.status === 'whatsapp' ? "success" : "primary"}
                    size="small"
                    onClick={() => this.handleWitnessWhatsApp(testemunha)}
                    sx={{ border: '1px solid', borderRadius: 1 }}
                  >
                    <WhatsApp />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => this.handleRemoveTestemunha(testemunha.id)}
                  >
                    <Remove />
                  </IconButton>
                </Stack>
              </Box>

              {/* Abre o canvas para esta testemunha apenas se selecionado o botão "No PC" */}
              {showWitnessPadId === testemunha.id && (
                <SignaturePad
                  title={`Assinatura da ${testemunha.label}`}
                  onSave={(dataUrl) => this.handleWitnessSave(testemunha.id, dataUrl)}
                  onClear={() => this.handleWitnessSave(testemunha.id, null)}
                  onClose={() => this.setState({ showWitnessPadId: null })}
                />
              )}

              {/* Status da testemunha */}
              {showWitnessPadId !== testemunha.id && testemunha.status && (
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1, fontWeight: '600' }}>
                  {testemunha.status === 'whatsapp' && "✓ Link da testemunha enviado via WhatsApp."}
                  {testemunha.status === 'qrcode' && "✓ QR Code da testemunha visualizado."}
                </Typography>
              )}
            </Paper>
          ))}

          {/* Botão para Criar Nova Testemunha */}
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={this.handleAddWitnessRow}
            fullWidth
            sx={{ mb: 4, borderStyle: "dashed" }}
          >
            Adicionar Testemunha
          </Button>

          {/* Footer de Salvar Geral */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={this.handleFinalSave}
              disabled={!locatarioStatus} // Libera se o locatário escolheu alguma opção (Local, WhatsApp ou QR Code)
              size="large"
            >
              Salvar Documento
            </Button>
          </Stack>
        </Box>

        {/* MODAL DO QR CODE */}
        <Dialog open={shareModal.open} onClose={this.handleCloseShareModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: '600' }}>{shareModal.title}</DialogTitle>
          <DialogContent dividers sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aponte a câmera do celular para o código abaixo:
            </Typography>

            <Box
              sx={{
                width: 180,
                height: 180,
                border: '1px solid #ccc',
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2
              }}
            >
              <QrCode2 sx={{ fontSize: 140, color: '#333' }} />
            </Box>

            <Typography variant="caption" color="primary" sx={{ wordBreak: 'break-all', display: 'block', mt: 1 }}>
              {shareModal.link}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseShareModal} color="primary" variant="contained">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default Assinatura;